import React, { useState, useEffect } from 'react';
import { stop } from '../../utils';
import config from '../internetConfig';
import __ from '../internetLanguage';
import PaginationItem from './PaginationItem.js';

export default function Pagination(props) {
	const page = parseInt(props.page || 1, 10);
	const totalResults = parseInt(props.totalResults || 1, 10);
	const anchor = props.anchor || '#results';
	const goToPage = props.goToPage || function () {};
	const totalPages = Math.ceil(totalResults / config.pageSize);
	const prev = 1 < page ? page - 1 : false;
	const next = page < totalPages ? page + 1 : false;
	const firstPageThreshold = 3;

	// Max 5 elements: 3 numeric, 1 ellipsis, 1 final page
	const totalElementsToShow = 5; // max number of page links to show
	const reserveForLast = 1; // reserve one slot for the last page
	const reserveForEllipsis = 1; // reserve one slot for ellipsis
	const pagesShown = totalElementsToShow - reserveForLast - reserveForEllipsis;
	const nearEndThreshold = totalElementsToShow - reserveForLast;

	let pages = [];

	if (totalPages <= totalElementsToShow) {
		// If total pages are small, show all
		for (let i = 1; i <= totalPages; i++) {
			pages.push(i);
		}
	} else if (page >= totalPages - nearEndThreshold) {
		// Near end: show full last block
		const rangeStart = Math.max(1, totalPages - (totalElementsToShow - 1));
		for (let i = rangeStart; i < totalPages; i++) {
			pages.push(i);
		}
	} else {
		// Mid-range: show 3 dynamic + ellipsis + last
		const range = Math.floor(pagesShown / 2);

		// Start by centering current page
		let rangeStart = page - range;

		// Clamp start so it doesn’t go below 1
		rangeStart = Math.max(1, rangeStart);

		// If the block would go past the final page (excluding final reserved slot), shift it back
		if (rangeStart + pagesShown > totalPages - 1) {
			rangeStart = totalPages - 1 - pagesShown;
		}

		// Ensure it never goes negative again
		rangeStart = Math.max(1, rangeStart);

		// Fill the range
		for (let i = rangeStart; i < rangeStart + pagesShown && i < totalPages; i++) {
			pages.push(i);
		}

		pages.push('ellipsis');
	}

	// Always add final page
	if (!pages.includes(totalPages)) {
		pages.push(totalPages);
	}

	const first = !pages.includes(1) ? 1 : false;

	return (
		<nav aria-label="Search Results Pagination" className="nav d-flex justify-content-end">
			{totalResults > config.pageSize ? (
				<ul className="pagination">
					{first ? (
						<PaginationItem
							className="page-item page-item--first"
							dataPage={first}
							href={anchor}
							onClick={(e) => stop(e) && goToPage(first, anchor)}
							label={
								<>
									<span aria-label={__('firstPage')} className="cdc-fa-angles-left"></span>
									<span> {__('first')}</span>
								</>
							}
						/>
					) : (
						''
					)}

					<PaginationItem
						className={'page-item page-item--prev' + (prev ? '' : ' disabled')}
						dataPage={prev}
						href={anchor}
						onClick={(e) => stop(e) && goToPage(prev, anchor)}
						label={<span aria-label={__('previousPage')} className="cdc-fa-angle-left"></span>}
					/>

					{pages.map((pageIndex, i) => {
						if (pageIndex === 'ellipsis') {
							return (
								<PaginationItem
									key={`ellipsis-${i}`}
									className="page-item disabled"
									ariaDisabled={true}
									label={<span>…</span>}
								/>
							);
						}
						return (
						<PaginationItem
							key={pageIndex}
							className={'page-item page-num' + (page === pageIndex ? ' active' : '')}
							dataPage={pageIndex}
							href={anchor}
							onClick={(e) => page !== pageIndex && stop(e) && goToPage(pageIndex, anchor)}
							label={pageIndex}
						/>
						);
					})}

					<PaginationItem
						className={`page-item page-item--next ${next ? '' : ' disabled'} `}
						dataPage={next}
						href={anchor}
						onClick={(e) => next && stop(e) && goToPage(next, anchor)}
						label={<span aria-label={__('nextPage')} className="cdc-fa-angle-right"></span>}
					/>
				</ul>
			) : (
				''
			)}
		</nav>
	);
}
